/**
 * The changelog ("What's new") feed for The Sweet Spot.
 *
 * Read-only here. Entries are authored in Cockpit at /it/changelog by holders of
 * the `it.changelog` grant. Backend api#541, migration 0153.
 *
 * The read endpoint deliberately carries no capability and only requires a
 * signed-in principal, so the Google store accounts this site runs on can read
 * it. Anyone who can see the avatar menu can see this page.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, type ApiError } from '@/lib/api';
import { API_BASE } from '@/lib/apiBase';

const CHANGELOG_BASE = `${API_BASE}/v1/core/changelog`;

export type ChangelogToolRef = {
  app: 'cockpit' | 'sweetspot';
  tool_id: string;
  tool_label: string;
};

export type ChangelogEntry = {
  id: number;
  app: 'cockpit' | 'sweetspot' | 'both';
  title: string;
  body_html: string;
  /** True when the entry covers the whole site, so `tools` is empty. */
  all_tools: boolean;
  published_at: string;
  author_email: string;
  tools: ChangelogToolRef[];
};

/** A stable key for a tool tag, since ids can collide across the two apps. */
export function changelogToolKey(t: ChangelogToolRef): string {
  return `${t.app}:${t.tool_id}`;
}

/**
 * The Sweet Spot feed, newest first. Entries published to `both` are included by
 * the API. Returns an empty list on a 404 so the page shows its empty state
 * rather than an error while the backend ships in parallel.
 */
export function useChangelog(): UseQueryResult<ChangelogEntry[], Error> {
  return useQuery<ChangelogEntry[], Error>({
    queryKey: ['changelog', 'sweetspot'],
    queryFn: async () => {
      try {
        const res = await apiGet<{ entries: ChangelogEntry[] }>(`${CHANGELOG_BASE}?app=sweetspot`);
        return res.entries;
      } catch (err) {
        if ((err as ApiError).status === 404) return [];
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}
